import type { RouteKey } from "@/lib/chat/routes";

/**
 * The questions the assistant offers, per topic.
 *
 * Unlike the other records here these are not facts the assistant asserts —
 * they are the visitor's own words, put in their mouth. That makes the tone a
 * business decision rather than a copy decision:
 *
 *   - They are the questions a buyer actually has: does this fit us, who does
 *     the work, what does it cost us in effort, where does our data go. Not
 *     "Wollen Sie ein Angebot?".
 *   - `opening` runs before anything is known, so it must be answerable from
 *     the knowledge base alone. A question the assistant can only deflect is a
 *     bad first impression.
 *   - `followUp` deepens the topic rather than steering toward contact. The
 *     visitor decides when the conversation turns into an enquiry; the
 *     assistant's job before then is to be worth reading.
 *
 * Each question carries a note saying why it is offered and what it is worth —
 * to the visitor and to the business. If a question cannot be given one, it
 * probably should not be on the list.
 *
 * The two languages are paired per question rather than held in two parallel
 * arrays, so a translation cannot drift out of order from what it translates.
 */

export interface Suggestion {
  readonly de: string;
  readonly en: string;
}

export interface SuggestionSet {
  /** Offered when the conversation is empty. Answerable from the records. */
  readonly opening: readonly Suggestion[];
  /** Offered after an answer on this topic. */
  readonly followUp: readonly Suggestion[];
}

/** Used where a page has no topic of its own — home, blog, legal pages. */
export const DEFAULT_SUGGESTIONS: SuggestionSet = {
  /*
   * One question per thing people actually arrive wanting built — an app, a
   * chatbot on their own material, a manual routine that should stop being
   * manual — followed by the second question each of those topics raises.
   *
   * Questions about the company ("Was macht HAINTZ genau?", "Wer arbeitet an
   * unserem Projekt?") were honest but abstract: they ask the visitor to be
   * interested in us before we have shown we can do anything. These name the
   * job instead, and every one of them can be answered in full from the records
   * — which rules out the obvious opener, "Was kostet eine App?": pricing is a
   * hard boundary (see BOUNDARIES "no-pricing", which names a Flutter-app
   * figure as its worked example of a wrong answer), so it could only ever be
   * deflected. A deflection is a poor first impression.
   *
   * The first three are one per topic and are what the narrow docked panel
   * shows; the last two deepen the same three topics. See Suggestions.tsx.
   */
  opening: [
    // First, because it is the objection that ends the conversation before it
    // starts, and because the answer is the whole positioning: what actually
    // stops SME AI projects — not cost, the fear that the
    // company's documents end up on someone else's servers. Answerable with a
    // plain no: EU-hosted, or on-premise with Ollama where it must not leave
    // the building. Names the product people picture, because that is the shape
    // the worry has.
    {
      de: "Muss ich meine Daten für einen Chatbot zu ChatGPT hochladen?",
      en: "Do I have to upload my data to ChatGPT for a chatbot?",
    },
    // The most expensive misconception a visitor carries in, and the answer is
    // a differentiator rather than a caveat: one Flutter codebase, both stores.
    {
      de: "Muss eine App für iOS und Android zweimal entwickelt werden?",
      en: "Does an app have to be built twice, for iOS and Android?",
    },
    // The n8n question, asked the way it is really felt: somebody sorts mail
    // and files by hand every morning. Naming the drudgery rather than the tool
    // is what makes a visitor recognise their own company in it.
    {
      de: "Lassen sich E-Mails und Dateien automatisch verarbeiten?",
      en: "Can emails and files be processed automatically?",
    },
    // The follow-on worry of anyone who already runs an ERP or a warehouse
    // system: an app is only interesting if it talks to what is already there.
    {
      de: "Können wir eine App auf unser bestehendes System aufsetzen?",
      en: "Can we build an app on top of our existing system?",
    },
    // n8n's essence in the customer's words, for the visitor who senses there
    // is something to automate but cannot name a single process.
    {
      de: "Kann ein System Aufgaben zwischen unseren Tools weiterreichen?",
      en: "Can a system pass tasks between our tools?",
    },
  ],
  followUp: [
    // Turns interest into a picture of what working together looks like, which
    // is the step people take just before asking for a quote.
    {
      de: "Wie läuft die Zusammenarbeit ab?",
      en: "How does working together actually go?",
    },
    // The objection most likely to kill an AI project. Far better raised here
    // than after a proposal has been written.
    {
      de: "Wo liegen unsere Daten dabei?",
      en: "Where does our data live in that setup?",
    },
  ],
};

export const SUGGESTIONS: Partial<Record<RouteKey, SuggestionSet>> = {
  "solutions-ai-automation": {
    opening: [
      // Most visitors want AI but cannot name a use case. This hands them one
      // instead of a pitch.
      {
        de: "Wofür lohnt sich KI in einem KMU wirklich?",
        en: "Where does AI genuinely pay off in an SME?",
      },
      // Describes RAG in the customer's own words, and it happens to be the
      // highest-value service on the site.
      {
        de: "Kann ein System auf unseren eigenen Dokumenten antworten?",
        en: "Can a system answer from our own documents?",
      },
      // Austrian SMEs ask this before anything else. A yes, via on-premise
      // Ollama, removes the blocker that usually ends the conversation.
      {
        de: "Geht das auch, ohne dass Daten das Haus verlassen?",
        en: "Is that possible without data leaving our building?",
      },
    ],
    followUp: [
      // Naming honest failure modes reads as experience rather than marketing,
      // and quietly qualifies the visitor at the same time.
      {
        de: "Woran scheitern solche Projekte üblicherweise?",
        en: "What usually makes these projects fail?",
      },
      // Sets the effort expectation early, so the customer's own workload is
      // not a nasty surprise halfway through.
      {
        de: "Was müssen wir selbst beisteuern?",
        en: "What would we have to contribute ourselves?",
      },
    ],
  },

  "solutions-web-apps": {
    opening: [
      // Separates a real application from a website — which is exactly where
      // budgets get misjudged in both directions.
      {
        de: "Wofür eignet sich eine eigene Web-Applikation?",
        en: "What is a custom web application actually for?",
      },
      // Most work is a rewrite, not a greenfield build. This names the case the
      // majority of visitors are actually in.
      {
        de: "Können Sie eine bestehende Anwendung modernisieren?",
        en: "Can you modernise an application we already have?",
      },
      // Cheap to answer, and it stops a visitor comparing this against a web
      // design agency on price.
      {
        de: "Was unterscheidet das von einer Website?",
        en: "How is that different from a website?",
      },
    ],
    followUp: [
      // Integration is usually where the real scope of a project hides.
      {
        de: "Wie geht das mit unseren bestehenden Systemen zusammen?",
        en: "How does that fit with the systems we already run?",
      },
      // Raises maintenance: a genuine customer worry, and recurring work.
      {
        de: "Was passiert nach dem Livegang?",
        en: "What happens after it goes live?",
      },
    ],
  },

  "solutions-mobile-apps": {
    opening: [
      // Offering the "and when not" half earns more trust than any promise, and
      // filters out the projects that would have disappointed everyone.
      {
        de: "Wann ist eine eigene App sinnvoll — und wann nicht?",
        en: "When does a custom app make sense — and when not?",
      },
      // Turns a technology choice into what it means commercially: one
      // codebase, both stores, one budget.
      {
        de: "Was bedeutet Flutter für uns konkret?",
        en: "What does Flutter mean for us in practice?",
      },
      // Reassures anyone who already has systems that they are not starting
      // from zero — which is what makes the project affordable.
      {
        de: "Können Sie an unser bestehendes Backend anbinden?",
        en: "Can you connect to the backend we already have?",
      },
    ],
    followUp: [
      // Store publishing is the part customers quietly dread. Taking it on is a
      // concrete reason to hire someone.
      {
        de: "Wer übernimmt die Veröffentlichung in den Stores?",
        en: "Who handles publishing to the stores?",
      },
      // Apps need ongoing work. Saying so early avoids a difficult conversation
      // six months in.
      {
        de: "Wie wird die App später gewartet?",
        en: "How is the app maintained afterwards?",
      },
    ],
  },

  "solutions-backend": {
    opening: [
      // Lets a visitor diagnose themselves instead of being told they have a
      // problem, which is the difference between advice and a sales call.
      {
        de: "Woran merken wir, dass unser Backend an Grenzen stößt?",
        en: "How do we know our backend is hitting its limits?",
      },
      // Integration is common, lower risk to buy than a rebuild, and often the
      // way into a larger engagement.
      {
        de: "Können Sie bestehende Systeme integrieren?",
        en: "Can you integrate the systems we already have?",
      },
      // Frames an API as a business decision rather than a technical one, so a
      // non-technical decision-maker can weigh it.
      {
        de: "Wann lohnt sich eine eigene API?",
        en: "When is a custom API worth it?",
      },
    ],
    followUp: [
      // Legacy is the fear behind most backend enquiries; a calm answer is
      // worth more than a feature list.
      {
        de: "Wie gehen Sie mit Altsystemen um?",
        en: "How do you deal with legacy systems?",
      },
      // Moves the conversation to total cost, which is where trust is won or
      // lost with a careful buyer.
      {
        de: "Was heißt das für Betrieb und Wartung?",
        en: "What does that mean for running and maintaining it?",
      },
    ],
  },

  "solutions-devops": {
    opening: [
      // Translates an acronym into daily benefit — the only form a
      // decision-maker can actually act on.
      {
        de: "Was bringt uns CI/CD im Alltag?",
        en: "What does CI/CD change day to day?",
      },
      // Inviting a "no" is disarming, and it separates this from vendors whose
      // business model is selling complexity.
      {
        de: "Brauchen wir wirklich Kubernetes?",
        en: "Do we genuinely need Kubernetes?",
      },
      // A hard requirement for many Austrian firms. Answering it early is what
      // keeps them reading.
      {
        de: "Können wir in Europa hosten?",
        en: "Can we host in Europe?",
      },
    ],
    followUp: [
      // Migration, not the target state, is the real question behind every
      // infrastructure enquiry.
      {
        de: "Wie kommen wir von unserem jetzigen Setup dorthin?",
        en: "How do we get there from our current setup?",
      },
      // Surfaces the ongoing arrangement, which is the larger and longer part
      // of the value on both sides.
      {
        de: "Wer betreibt das danach?",
        en: "Who runs it afterwards?",
      },
    ],
  },

  "solutions-consulting": {
    opening: [
      // Consulting stays abstract until examples are given, and nobody buys
      // something abstract.
      {
        de: "Wobei hilft eine unabhängige Beratung konkret?",
        en: "What does independent advice actually help with?",
      },
      // A small, low-risk first engagement — probably the easiest yes anywhere
      // on the site, and it often reveals a much larger problem.
      {
        de: "Können Sie ein Angebot eines Dienstleisters prüfen?",
        en: "Can you review a quote from another vendor?",
      },
      // Speaks to companies who already have a system and suspect something is
      // wrong with it but cannot say what.
      {
        de: "Wie bewerten Sie eine bestehende Architektur?",
        en: "How do you assess an architecture we already have?",
      },
    ],
    followUp: [
      // Makes an intangible service concrete, which is precisely what makes it
      // purchasable.
      {
        de: "Was bekommen wir am Ende in die Hand?",
        en: "What do we end up holding in our hands?",
      },
      // Catches the visitor whose project is already in trouble — the most
      // urgent buyer there is.
      {
        de: "Können Sie ein laufendes Projekt begleiten?",
        en: "Can you oversee a project already under way?",
      },
    ],
  },

  "ai-readiness": {
    opening: [
      // Anyone hovering over the check wants this answered before spending five
      // minutes on it.
      {
        de: "Was misst dieser Check eigentlich?",
        en: "What does this check actually measure?",
      },
      // The question the page exists to answer, which leads into the check
      // without anyone having to push it.
      {
        de: "Woran erkennen wir, ob wir für KI bereit sind?",
        en: "How do we tell whether we are ready for AI?",
      },
      // Removes the suspicion that the check is just a lead-capture form with
      // extra steps.
      {
        de: "Was tun wir mit dem Ergebnis?",
        en: "What do we do with the result?",
      },
    ],
    followUp: [
      // Converts a score into an action. Without this the check is a number
      // nobody does anything with.
      {
        de: "Was wäre ein sinnvoller erster Schritt?",
        en: "What would a sensible first step be?",
      },
      // Sends a visitor who turns out to be ready onward to concrete use cases.
      {
        de: "Wofür lohnt sich KI in einem KMU wirklich?",
        en: "Where does AI genuinely pay off in an SME?",
      },
    ],
  },

  "tech-radar": {
    opening: [
      // A list of technologies proves nothing. The reasoning behind it is the
      // part that demonstrates judgement.
      {
        de: "Warum genau diese Technologien?",
        en: "Why these technologies in particular?",
      },
      // Being straight about the difference between used and merely watched is
      // itself a credibility signal.
      {
        de: "Was setzen Sie ein, was beobachten Sie nur?",
        en: "What is in use, and what are you only watching?",
      },
      // Customers are buying decisions, not tools. This shows how the decisions
      // get made.
      {
        de: "Wie entscheiden Sie sich für einen Stack?",
        en: "How do you settle on a stack?",
      },
    ],
    followUp: [
      // Fear of being left with unmaintainable software is a real blocker, and
      // rarely said out loud.
      {
        de: "Was heißt das für die Langlebigkeit unseres Projekts?",
        en: "What does that mean for how long our project lasts?",
      },
      // Stops a visitor ruling themselves out because their stack looks
      // different from the radar.
      {
        de: "Arbeiten Sie auch mit unserem bestehenden Stack?",
        en: "Would you work with the stack we already have?",
      },
    ],
  },

  blog: {
    opening: [
      // Orients a reader who landed on a single post from a search engine.
      {
        de: "Worüber schreiben Sie hier?",
        en: "What do you write about here?",
      },
      // Blog traffic rarely knows the company at all; this is the shortest way
      // to fix that before they leave.
      {
        de: "Was macht HAINTZ IT-SOLUTIONS genau?",
        en: "What exactly does HAINTZ IT-SOLUTIONS do?",
      },
      // Turns a casual reader into someone weighing whether this is for them.
      {
        de: "Passt das zu einem Unternehmen unserer Größe?",
        en: "Is this a fit for a company of our size?",
      },
    ],
    followUp: DEFAULT_SUGGESTIONS.followUp,
  },

  partners: {
    opening: [
      // The literal question this page raises in a visitor's mind.
      {
        de: "Mit wem arbeiten Sie zusammen?",
        en: "Who do you work with?",
      },
      // Partnerships only matter to a customer if they can see what they get
      // out of them.
      {
        de: "Was bedeutet das für ein Projekt mit uns?",
        en: "What does that mean for a project with us?",
      },
      // Protects the core promise: partners do not mean the contact changes or
      // that the customer ends up dealing with someone else.
      {
        de: "Wer arbeitet tatsächlich an unserem Projekt?",
        en: "Who actually works on our project?",
      },
    ],
    followUp: DEFAULT_SUGGESTIONS.followUp,
  },

  datenschutz: {
    opening: [
      // A fair question to ask on this page of all pages, and answering it
      // plainly is itself the proof the page is claiming.
      {
        de: "Wo werden meine Fragen an diesen Assistenten verarbeitet?",
        en: "Where are my questions to this assistant processed?",
      },
      // Turns a legal page into a reason to buy the AI service rather than a
      // dead end.
      {
        de: "Wie halten Sie KI-Lösungen DSGVO-konform?",
        en: "How do you keep AI solutions GDPR-compliant?",
      },
      // On-premise is the strongest answer available anywhere on the site, and
      // this is the page where it lands hardest.
      {
        de: "Geht das auch, ohne dass Daten das Haus verlassen?",
        en: "Is that possible without data leaving our building?",
      },
    ],
    followUp: [
      // Makes an abstract promise concrete enough for someone to evaluate.
      {
        de: "Was heißt On-Premise in der Praxis?",
        en: "What does on-premise mean in practice?",
      },
      // The lighter alternative for firms that do not actually need full
      // on-premise, so nobody is talked into more than they need.
      {
        de: "Können wir in Europa hosten?",
        en: "Can we host in Europe?",
      },
    ],
  },
};
